import { AppState as TechnicianAppState } from "@/models/TechnicianAppState";
import { AppState as SkillAssessmentAppState } from "@/models/SkillAssessmentAppState";
import { STATE_MODULES } from '@/store/state-modules';
import { ConfigMutations } from "@/store/modules/config/mutations";
import { generals } from "@puls/puls-general-utils";
import axios, { AxiosError, AxiosResponse, InternalAxiosRequestConfig } from "axios";
import { Store } from "vuex";
import config from '@/config';
import { LOGIN_CODE_VERIFICATION_URL } from '@/store/modules/login/actions';
import utils from './utils';
import { skillAssessmentConstants } from "@puls/puls-common-constants";
const HEADER_CONTEXT_NAME = "x-request-request-context";
const requests = new Set<string>();

export enum TokenStrategy {
	TechnicianAppToken, SkillAssessmentAppToken
}

interface IntreceptorOptions {
	token_strategy: TokenStrategy,
	token?: string
}

export default (store: Store<TechnicianAppState | SkillAssessmentAppState>) => (options: IntreceptorOptions) => {
	axios.interceptors.request.use(
		(requestConfig: InternalAxiosRequestConfig<any>) => {
			const uuid = generals.generateUuid();
			requests.add(uuid);
			// dont put the autherization header if already present and if no going to webapi
			switch (options.token_strategy) {
			case TokenStrategy.SkillAssessmentAppToken:
				requestConfig.headers[skillAssessmentConstants.SKILL_ASSESSMENT_TOKEN_HEADER_NAME] = options.token;
				break;
			case TokenStrategy.TechnicianAppToken:
			default:
				if (!requestConfig.headers.Authorization && requestConfig.url?.indexOf(config.api) !== -1) {
				// take the jwt from the store
					const jwt = (store.state as TechnicianAppState).login.userJWT;
					if (jwt) {
						requestConfig.headers.Authorization = `Bearer ${jwt}`;
					}
				}
				break;
			}
			
			requestConfig.headers[HEADER_CONTEXT_NAME] = uuid;

			if (requests.size) {
				store.commit(`${STATE_MODULES.CONFIG}/${ConfigMutations.UPDATE_HTTP_PROGRESS_INDICATOR}`, true);
			}

			return requestConfig;
		},
		(error: AxiosError) => {
			const uuid = error.config?.headers[HEADER_CONTEXT_NAME];
			requests.delete(uuid);
			if (!requests.size) {
				store.commit(`${STATE_MODULES.CONFIG}/${ConfigMutations.UPDATE_HTTP_PROGRESS_INDICATOR}`, false);
			}
			return Promise.reject(error);
		}
	);

	axios.interceptors.response.use(
		(response: AxiosResponse) => {
			const uuid = response.config?.headers[HEADER_CONTEXT_NAME];
			requests.delete(uuid);
			if (!requests.size) {
				store.commit(`${STATE_MODULES.CONFIG}/${ConfigMutations.UPDATE_HTTP_PROGRESS_INDICATOR}`, false);
			}
			
			handle401Status(response)
			
			return response;
		},
		(error: AxiosError<any, any>) => {
			const uuid = error.config?.headers[HEADER_CONTEXT_NAME];
			requests.delete(uuid);
			if (!requests.size) {
				store.commit(`${STATE_MODULES.CONFIG}/${ConfigMutations.UPDATE_HTTP_PROGRESS_INDICATOR}`, false);
			}

			const response: AxiosResponse | null = error.response || null;
			if (response) {
				handle401Status(response)
			}
			const errorMessage = error.response?.data?.message
			if (errorMessage) {
				error.message = errorMessage + ' | ' + error.message;
			}
			return Promise.reject(error);
		}
	);
}

function handle401Status (response: AxiosResponse) {
	if (response.status === 401 && response.config.url?.indexOf(LOGIN_CODE_VERIFICATION_URL) === -1) {
		utils.logout();
	}
}
